import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';

import {
  archiveCostCode,
  bulkArchiveCostCodes,
  updateCostCode,
  createCostCodes,
  deleteCostCodeById,
  bulkDeleteCostCodes,
} from './state/costcodes.actions';

import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import CardGrid from '../common/cardGrid/cardGrid';
import CostCodeAddView from './CostCodeAddView';
import CostCodeEditView from './CostCodeEditView';
import CostCodeCard from './costcodeCard';
import FilterDropdown from '../common/FilterDropdown';

import Permissions from '../auth/Permissions';

import Analytics from '../helpers/Analytics';
import { getHighestNumber } from '../helpers/helpers';
import { request } from '../helpers/requests';

import { createNuxEntry } from '../nux/state/nux.actions';
import { COSTCODE_GLOBAL_TYPE } from '../nux/nux.constants';
import { formatCustomFieldCreatePayload, getCostcodeUpdatePayload } from '../helpers/costcodeHelpers';
import { currencyFormatter } from '../helpers/inputParsers';
import TagRender from '../common/TagRender';
import {
  getDefaultFilters,
  getFilterData,
  searchCostcodes,
  searchTieBreaker,
} from './costcodeFilter';
import createCostcodeSortFilterController from './costcodeSortFilterController';

const FIELDS_BY_STEP = [
  ['code', 'name', 'description', 'categoryIds'],
];

export const columnsToDisplay = (categoriesIdMap = {}) => [
  {
    title: 'Cost Code',
    dataIndex: 'code',
    key: 'code',
    width: 100,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: 100,
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    width: 150,
  },
  {
    title: 'Division',
    dataIndex: 'division',
    key: 'division',
    width: 100,
  },
  {
    title: 'Categories',
    dataIndex: 'categoryIds',
    key: 'categoryIds',
    width: 150,
    render: (_, record) => {
      const { categoryIds = [] } = record ?? {};
      return (
        <TagRender
          tagMap={categoriesIdMap}
          tagIds={categoryIds}
          useRedesign
        />
      );
    },
  },
  {
    title: 'Hourly Wage',
    dataIndex: 'hourlyWage',
    key: 'hourlyWage',
    width: 100,
    render: (_, record) => (record.hourlyWage ? currencyFormatter(record.hourlyWage) : ''),
  },
  {
    title: 'Daily Wage',
    dataIndex: 'dailyWage',
    key: 'dailyWage',
    width: 100,
    render: (_, record) => (record.dailyWage ? currencyFormatter(record.dailyWage) : ''),
  },
  {
    title: 'Hourly Billing Rate',
    dataIndex: 'hourlyBillingRate',
    key: 'hourlyBillingRate',
    width: 125,
    render: (_, record) => (record.hourlyBillingRate ? currencyFormatter(record.hourlyBillingRate) : ''),
  },
  {
    title: 'Daily Billing Rate',
    dataIndex: 'dailyBillingRate',
    key: 'dailyBillingRate',
    width: 125,
    render: (_, record) => (record.dailyBillingRate ? currencyFormatter(record.dailyBillingRate) : ''),
  },
];

export default connect(
  (state, ownProps) => ({
    ...ownProps,
    selectedDivisionIds: state.settings.selectedDivisions,
    nux: state.nux.nux,
    company: state.settings.company,
    divisions: state.settings.divisions,
    categoriesIdMap: state.costcodes.categoriesIdMap,
    useRedesign: state.settings.company?.settings?.uxRedesignPt1,
    templates: state.costcodes.templates,
  }),
  {
    archiveCostCode,
    bulkArchiveCostCodes,
    createCostCodes,
    updateCostCode,
    deleteCostCodeById,
    bulkDeleteCostCodes,
    createNuxEntry,
  },
)(withRouter(class CostCodesList extends React.Component {
  constructor(props) {
    super(props);

    this.sortFilter = createCostcodeSortFilterController({
      storageKeyText: 'costcodesSortText',
      storageKeyActive: 'costcodesSortActive',
      displayStateKey: 'displayCodes',
      getFetchedCodes: () => this.state.fetchedCodes,
      getArchivedLoaded: () => this.state.archivedLoaded,
      setState: (update) => this.setState(update),
      onFetchArchived: () => this.fetchGlobalCodes({ archivedOnly: true }),
    });

    this.state = {
      fetchedCodes: [],
      displayCodes: [],
      loading: false,
      errors: {},
      customFields: [],
      initializedCustomCategoryFilters: false,
      archivedLoaded: false,
      ...this.sortFilter.getInitialState(),
    };

    this.onExportCSV = this.exportCSV.bind(this);
    this.onExportExcel = this.exportExcel.bind(this);
    this.onAdd = this.add.bind(this);
    this.onUpdate = this.update.bind(this);
    this.onArchive = this.archive.bind(this);
    this.onDelete = this.delete.bind(this);
  }

  componentDidMount() {
    const {
      createNuxEntry,
      setBreadcrumb,
      nux = new Set(),
      location: {
        state: {
          targetId: locationTargetId,
        } = {},
      } = {},
    } = this.props;

    setBreadcrumb([{
      text: 'Cost Codes',
      icon: 'global',
    }, {
      text: 'Global Codes',
    }]);

    if (!nux.has(COSTCODE_GLOBAL_TYPE)) {
      createNuxEntry(COSTCODE_GLOBAL_TYPE);
    }

    if (this.cardRef && locationTargetId) {
      this.cardRef.targetItem = { id: locationTargetId };
    }

    this.fetchGlobalCodes();
  }

  componentDidUpdate(prevProps) {
    const {
      selectedDivisionIds = new Set(),
      categoriesIdMap = {},
    } = this.props;

    const {
      selectedDivisionIds: prevSelectedDivisionIds = new Set(),
      categoriesIdMap: prevCategoriesIdMap = {},
    } = prevProps;

    const {
      initializedCustomCategoryFilters,
    } = this.state;

    if (prevSelectedDivisionIds !== selectedDivisionIds) {
      this.setState({ archivedLoaded: false });
      this.fetchGlobalCodes();
    }

    if (prevCategoriesIdMap !== categoriesIdMap && !initializedCustomCategoryFilters) {
      this.sortFilter.filter.setDefaultFilters(getDefaultFilters(categoriesIdMap));
      this.setState({ initializedCustomCategoryFilters: true });
      this.sortFilter.applyFilters();
    }
  }

  refetch() {
    const { archivedLoaded } = this.state;
    // When archived codes have been loaded, re-fetch both active and archived
    // so the merged set stays fresh after any mutation.
    this.fetchGlobalCodes({ archivedOnly: archivedLoaded });
  }

  async add(values) {
    Analytics.track('Costcode/Create/Global');
    const {
      createCostCodes,
    } = this.props;
    // Spreadsheet upload
    if (Array.isArray(values)) {
      const success = await createCostCodes(values);
      if (success) this.refetch();
      return success;
    }
    const {
      customFields,
    } = this.state;
    const payload = formatCustomFieldCreatePayload({ payload: values, customFields });

    if (payload?.error || !payload) {
      return this.setState({
        errors: payload?.errorMap,
      });
    }

    this.setState({
      errors: {},
    });
    const success = await createCostCodes(payload);
    if (success) this.refetch();
    return success;
  }

  async update(id, payload) {
    const {
      customFields,
      displayCodes: costcodes,
    } = this.state;
    const {
      updateCostCode,
      templates,
    } = this.props;

    const res = await getCostcodeUpdatePayload({
      id,
      payload,
      customFields,
      costcodes,
      templates,
    });

    if (res?.error || !res) {
      return this.setState({
        errors: res?.errorMap,
      });
    }

    this.setState({
      errors: {},
    });

    const result = await updateCostCode(id, res);
    this.refetch();
    return result;
  }

  archive(costcode) {
    const {
      archiveCostCode,
    } = this.props;
    return new Promise((resolve) => {
      const mode = costcode.active ? 'Archive' : 'Activate';

      CustomConfirmModal({
        title: `${mode} Cost Code ${costcode.code}- ${costcode.name}?`,
        okText: mode,
        cancelText: 'Cancel',
        async onOk() {
          const result = await archiveCostCode(costcode.id, !costcode.active);
          resolve(result);
        },
        onCancel() {
          resolve();
        },
      });
    }).then((result) => {
      if (result !== undefined) this.refetch();
      return result;
    });
  }

  delete(costcode) {
    const {
      deleteCostCodeById,
    } = this.props;

    return new Promise((resolve) => {
      CustomConfirmModal({
        title: `Delete Cost Code ${costcode.code}- ${costcode.name}?`,
        okText: 'Delete',
        cancelText: 'Cancel',
        async onOk() {
          const result = await deleteCostCodeById(costcode.id);
          resolve(result);
        },
        onCancel() {
          resolve();
        },
      });
    }).then((result) => {
      if (result !== undefined) this.refetch();
      return result;
    });
  }

  onBulkArchive = async (ids, { password }) => {
    const { bulkArchiveCostCodes: bulkArchive } = this.props;
    const result = await bulkArchive(ids, { password }, false);
    if (result) this.refetch();
    return result;
  }

  onBulkActivate = async (ids, { password }) => {
    const { bulkArchiveCostCodes: bulkArchive } = this.props;
    const result = await bulkArchive(ids, { password }, true);
    if (result) this.refetch();
    return result;
  }

  onBulkDelete = async (ids, { password }) => {
    const { bulkDeleteCostCodes: bulkDelete } = this.props;
    const result = await bulkDelete(ids, { password });
    if (result) this.refetch();
    return result;
  }

  export(type) {
    const {
      displayCodes,
    } = this.state;
    const {
      divisions = {},
      categoriesIdMap = {},
    } = this.props;
    Analytics.track('Costcode/Export', { ExportFileType: type });
    const workbook = XLSX.utils.book_new();
    const formattedCostcodes = displayCodes.map((costcode) => {
      const { [costcode.divisionId]: { name: division } = {} } = divisions;
      const categoryNames = costcode.categoryIds
        ?.map((categoryId) => categoriesIdMap[categoryId]?.name)
        ?.join(', ');
      return {
        Name: costcode.name,
        'Cost Code': costcode.code,
        Description: costcode.description,
        Categories: categoryNames,
        Division: division,
      };
    });
    const sheet = XLSX.utils.json_to_sheet(formattedCostcodes, {
      header: ['Name', 'Cost Code', 'Description', 'Division', 'Categories'],
    });
    XLSX.utils.book_append_sheet(workbook, sheet, 'Cost Codes');
    XLSX.writeFile(workbook, `OntraccrCostCodes.${type}`);
  }

  exportCSV() {
    this.export('csv');
  }

  exportExcel() {
    this.export('xlsx');
  }

  getMoreOptions() {
    return [
      {
        title: 'Export to Excel',
        icon: 'file-excel',
        onClick: this.onExportExcel,
      },
      {
        title: 'Export to CSV',
        icon: 'file-text',
        onClick: this.onExportCSV,
      },
    ];
  }

  async fetchGlobalCodes({ archivedOnly = false } = {}) {
    const {
      selectedDivisionIds = new Set(),
    } = this.props;
    const {
      fetchedCodes,
    } = this.state;
    if (!selectedDivisionIds.size) return;

    this.setState({ loading: true });
    const res = await request({
      call: axios.get('/costcodes/global', {
        params: {
          divisionIds: [...selectedDivisionIds],
          includeCategoryIds: true,
          ...(archivedOnly && { archivedOnly: true }),
          columns: [
            'description',
            'hourlyWage',
            'wageMultiplier',
            'dailyWage',
            'wageAdjustment',
            'hourlyBillingRate',
            'dailyBillingRate',
            'intuitId',
            'sageId',
            'eclipseId',
          ],
        },
      }),
      errMsg: 'Could not get global cost codes',
      hideSuccessToast: true,
    });
    const incoming = res?.data ?? [];
    const fetched = archivedOnly
      ? [...(fetchedCodes.filter((c) => c.active)), ...incoming]
      : incoming;
    this.sortFilter.setFilterData(fetched);
    this.setState({ fetchedCodes: fetched, loading: false, archivedLoaded: archivedOnly || false }, () => {
      this.sortFilter.applyPersistedSort();
    });
  }

  render() {
    const {
      company: {
        settings: {
          suggestNumbers,
        } = {},
      } = {},
      categoriesIdMap = {},
      useRedesign = false,
    } = this.props;

    const {
      displayCodes = [],
      loading,
      errors = {},
      customFields = [],
      sortActive,
      activeFilters = {},
      filterIsActive,
    } = this.state;

    const initialCode = suggestNumbers
      ? (getHighestNumber(displayCodes, 'code') + 1).toString()
      : null;

    const addView = (form) => (
      <CostCodeAddView
        {
          ...{
            ...form,
            initialCode,
          }
        }
        errors={errors}
        customFields={customFields}
        setCustomFields={(fields) => this.setState({ customFields: fields })}
      />
    );

    const editView = (form, formProps) => (
      <CostCodeEditView
        form={form}
        formProps={formProps}
        isProjectSpecific
        errors={errors}
        customFields={customFields}
        setCustomFields={(fields) => this.setState({ customFields: fields })}
      />
    );

    const filter = (
      <FilterDropdown
        filters={[getFilterData(categoriesIdMap)]}
        activeFilters={activeFilters}
        onFilter={(checkedFilters, filterCategory) => (
          this.sortFilter.filterCostcodes(checkedFilters, filterCategory)
        )}
        useRedesign={useRedesign}
      />
    );

    return (
      <CardGrid
        onRef={(ref) => { this.cardRef = ref; }}
        dataSource={displayCodes}
        title="Global Cost Codes"
        entity="Cost Code"
        itemView={CostCodeCard}
        itemDimensions={{ width: 239, height: 190 }}
        colCount={4}
        pageSize={12}
        add={Permissions.has('COSTCODES_WRITE') ? {
          title: 'Enter Cost Code Details',
          formView: addView,
          width: 'fit-content',
          onClose: this.onAdd,
          validateSteps: FIELDS_BY_STEP,
        } : null}
        edit={{
          title: 'Edit Cost Code Details',
          formView: editView,
          width: 800,
          onClose: this.onUpdate,
          canEdit: () => Permissions.has('COSTCODES_WRITE'),
          canArchive: () => Permissions.has('COSTCODES_WRITE'),
          canDelete: () => Permissions.has('COSTCODES_WRITE'),
          validateSteps: FIELDS_BY_STEP,
        }}
        onArchive={this.onArchive}
        onDelete={this.onDelete}
        bulkActions={Permissions.has('COSTCODES_WRITE') ? {
          canArchive: true,
          canActivate: true,
          canDelete: true,
          entityLabel: 'cost codes',
          warnings: {
            delete: {
              header: 'Deleting cost codes will uncode all time entries currently tied to this cost code.',
              subtext: 'Only archived cost codes will be deleted. This action is permanent.',
            },
          },
        } : null}
        onBulkArchive={this.onBulkArchive}
        onBulkActivate={this.onBulkActivate}
        onBulkDelete={this.onBulkDelete}
        search={{
          customRanking: searchCostcodes,
          tieBreaker: searchTieBreaker,
        }}
        more={this.getMoreOptions()}
        sort={{
          sortOptions: this.sortFilter.sortOptions,
          sortActive,
        }}
        filter={filter}
        filterActive={filterIsActive}
        isListView
        displayColumns={columnsToDisplay(categoriesIdMap)}
        isLoading={loading}
      />
    );
  }
}));
